import React, { useState, useEffect } from 'react';
import moment from 'moment';
import {
	LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, ReferenceLine
} from 'recharts';
import { ListingModel } from 'models/Listing/Listing';
import { MarketModel } from 'models/Market/Market';
import { YearlySummary, YearlySummaryItem } from 'models/Market/types';
import { MetricModel } from 'models/Market/Metric';
import {
	Flex,
	Text,
	Card,
	CardHeader,
	Box,
	Spacer
} from '@chakra-ui/react';
import { getLastKnownValueForYear, getLastKnownValueForMonth, getFullYearlyData, getFullDailyDataForMonth, getFullMonthlyDataForYear } from 'utils/market';
import { ActiveDot, DayDot, MonthDot, YearDot } from './Dots';
import { DayXTick, MonthXTick, YearXTick, PriceYTick } from './Ticks';
import { MarketPriceChartTooltip } from './Tooltips';
import { roundUpToSignificant } from 'utils/number';

interface MarketPriceChartProps {
	market: MarketModel | null;
	listings?: ListingModel[];
	startingPrice?: number | null;
}

const MarketPriceChart: React.FC<MarketPriceChartProps> = ({ market, listings = [], startingPrice = null }) => {
	const [selectedYear, setSelectedYear] = useState<number | null>(null);
	const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
	const [yearlyData, setYearlyData] = useState<YearlySummaryItem[]>([]);
	const [data, setData] = useState<YearlySummary | null>(null);
	const [lastKnownValue, setLastKnownValue] = useState<number | null>(startingPrice);

	useEffect(() => {
		if (market) {
			setData(market.yearlySummary);
			setYearlyData(market.getYearlyData());
		}
	}, [market]);

	useEffect(() => {
		if (selectedYear) {
			const lastKnownValueForSelectedYear = getLastKnownValueForYear(selectedYear, yearlyData);
			if (lastKnownValueForSelectedYear) {
				setLastKnownValue(lastKnownValueForSelectedYear);
			} else if (startingPrice) {
				setLastKnownValue(startingPrice);
			}
		}
	}, [selectedYear, yearlyData]);

	useEffect(() => {
		if (selectedYear && selectedMonth) {
			const lastKnownValueForSelectedMonth = getLastKnownValueForMonth(selectedYear, selectedMonth, yearlyData);
			if (lastKnownValueForSelectedMonth) {
				setLastKnownValue(lastKnownValueForSelectedMonth);
			} else if (startingPrice) {
				setLastKnownValue(startingPrice);
			}
		}
	}, [selectedMonth, yearlyData]);

	const handleDotClick = (payload: MetricModel | undefined) => {
		if (payload?.key) {
			if (selectedYear && !selectedMonth) {
				setSelectedMonth(Number(payload.key));
			} else if (!selectedYear) {
				const actualYearData = yearlyData.find(y => y.overview.key === Number(payload.key));
				if (actualYearData) {
					setSelectedYear(Number(payload.key));
				}
			}
		}
	};

	const getYDomain = (data: MetricModel[], startingPrice?: number | null | undefined) => {
		const values = data.map(d => d.mean);
		if (startingPrice !== null && startingPrice !== undefined) {
			values.push(startingPrice);
		}

		const roundedMax = roundUpToSignificant(Math.max(...values));
		return [0, roundedMax];
	};

	function renderTitle() {
		return (
			<Text textAlign="left" fontSize='md' fontWeight='bold' mb='6px'>
				Price History
			</Text>
		);
	}

	function renderChartBackButton() {
		if (selectedMonth) {
			return (
				<Flex direction={'row'} width={'100%'} paddingLeft={8}>
					<Text textAlign={'center'} fontSize={10} fontWeight={'bold'}>
						{moment(selectedMonth, 'M').format('MMMM')}, {selectedYear}
					</Text>
					<Spacer />
					<Text textAlign={'right'} fontSize={10} cursor={'pointer'} color={'blue.500'} fontWeight={'bold'} onClick={() => setSelectedMonth(null)} colorScheme="blue">
						Back to Monthly View
					</Text>
				</Flex>
			);
		} else if (selectedYear) {
			return (
				<Flex direction={'row'} width={'100%'} paddingLeft={8}>
					<Text textAlign={'left'} fontSize={'xs'} fontWeight={'bold'}>
						{selectedYear}
					</Text>
					<Spacer />
					<Text textAlign={'right'} fontSize={10} cursor={'pointer'} color={'blue.500'} fontWeight={'bold'} onClick={() => setSelectedYear(null)} colorScheme="blue">
						Back to Yearly View
					</Text>
				</Flex>
			);
		}
	}

	function renderChartData() {
		if (selectedYear && selectedMonth) {
			return (
				<LineChart data={getFullDailyDataForMonth(selectedYear, selectedMonth, listings, lastKnownValue)}>
					<XAxis dataKey="key" tick={DayXTick} />
					<YAxis tick={PriceYTick} />
					<Tooltip content={(props: any) => <MarketPriceChartTooltip selectedYear={selectedYear} selectedMonth={selectedMonth} listings={listings} {...props} />} />
					<Line name="Mean Price" dot={(props: any) => <DayDot dailyData={getFullDailyDataForMonth(selectedYear, selectedMonth, listings, lastKnownValue)} {...props} />} type="monotone" dataKey="mean" />
					<Legend iconSize={14} wrapperStyle={{ fontSize: "12px", fontWeight: "bold" }} />
					{startingPrice && <ReferenceLine y={startingPrice} style={{ fontSize: "12px", fontWeight: "bold" }} stroke="blue" strokeDasharray="3 3" />}
				</LineChart>
			);
		} else if (selectedYear) {
			return (
				<LineChart data={getFullMonthlyDataForYear(selectedYear, data, lastKnownValue)}>
					<XAxis dataKey="key" tick={MonthXTick} />
					<YAxis tick={PriceYTick} />
					<Tooltip content={(props: any) => <MarketPriceChartTooltip selectedYear={selectedYear} selectedMonth={selectedMonth} listings={listings} {...props} />} />
					<Line
						name="Mean Price"
						dot={(props: any) => <MonthDot monthlyData={getFullMonthlyDataForYear(selectedYear, data, lastKnownValue)} {...props} />}
						dataKey="mean"
						type="monotone"
						activeDot={<ActiveDot onClickHandler={handleDotClick} />}
					/>
					<Legend iconSize={14} wrapperStyle={{ fontSize: "12px", fontWeight: "bold" }} />
					{startingPrice && <ReferenceLine y={startingPrice} style={{ fontSize: "12px", fontWeight: "bold" }} stroke="blue" strokeDasharray="3 3" />}
				</LineChart>
			);
		} else {
			const fullYearlyData: MetricModel[] = data ? getFullYearlyData(data).map(yearData => yearData.overview) : [];
			return (
				<LineChart data={fullYearlyData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
					<XAxis dataKey="key" tickCount={12} tick={YearXTick} />
					<YAxis tick={PriceYTick} domain={getYDomain(fullYearlyData, startingPrice)} />
					<Tooltip content={(props: any) => <MarketPriceChartTooltip selectedYear={selectedYear} selectedMonth={selectedMonth} listings={listings} {...props} />} />
					<Line
						name="Mean Price"
						dot={(props: any) => {
							const { key, ...rest } = props;
							return <YearDot key={key} yearlyData={yearlyData} {...rest} />;
						}}
						dataKey="mean"
						type="monotone"
						activeDot={<ActiveDot onClickHandler={handleDotClick} />}
					/>
					<Legend iconSize={14} wrapperStyle={{ fontSize: "12px", fontWeight: "bold" }} />
					{startingPrice && <ReferenceLine y={startingPrice} style={{ fontSize: "12px", fontWeight: "bold" }} stroke="blue" strokeDasharray="3 3" />}
				</LineChart>
			)
		}
	}

	return (
		<Card paddingY={4} paddingRight={2} borderRadius={8} width={'100%'} display={'flex'} flexDirection={'column'}>
			<CardHeader paddingX={4} paddingY={0} mb={4}>
				<Flex
					direction='column'
					alignSelf='flex-start'>
					{renderTitle()}
				</Flex>
			</CardHeader>
			<Box w='100%' height="300px" justifyContent="center" alignItems="center">
				<ResponsiveContainer>
					{renderChartData()}
				</ResponsiveContainer>
			</Box>
			<Box minHeight={4} width="100%">
				{renderChartBackButton()}
			</Box>
		</Card>
	);
}

export default MarketPriceChart;
